import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Trash2, Share2, ShoppingBag } from "lucide-react";
import { api, money, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { SpendNudge } from "@/components/SpendNudge";
import { ProductImage } from "@/components/ProductImage";

export default function Cart() {
  const { items, setQty, removeItem, subtotal, charge, chargeLabel, grandTotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);

  const share = async () => {
    if (items.length === 0) return;
    setSharing(true);
    try {
      const { data } = await api.post("/baskets", {
        items: items.map((i) => ({ product_id: i.product.product_id, quantity: i.quantity })),
      });
      const url = data.url;
      if (navigator.share) {
        await navigator.share({ title: "My Pepperonni Market basket", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Basket link copied!");
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSharing(false);
    }
  };

  const checkout = () => {
    if (!user) {
      toast.info("Sign in to checkout");
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }
    navigate("/checkout");
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-40">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#F5F8F6] px-4 py-3 pt-safe flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#F5F8F6] flex items-center justify-center active:scale-90 transition-transform" data-testid="cart-back">
          <ArrowLeft size={20} className="text-[#122117]" />
        </button>
        <h1 className="text-xl font-extrabold text-[#122117]">Your Basket</h1>
      </header>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center px-8 py-24" data-testid="cart-empty">
          <div className="w-20 h-20 rounded-full bg-[#DDF0E5] flex items-center justify-center mb-4">
            <ShoppingBag size={34} className="text-[#0B7A3B]" />
          </div>
          <h2 className="font-extrabold text-lg text-[#122117]">Your basket is empty</h2>
          <p className="text-[#6B8375] mt-1">Add some fresh market goods to get started.</p>
          <button onClick={() => navigate("/market")} className="mt-6 bg-[#0B7A3B] text-white font-bold rounded-full px-6 py-3 active:scale-95 transition-transform" data-testid="cart-go-shop">
            Browse the market
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          <SpendNudge subtotal={subtotal} />

          <div className="space-y-2.5">
            {items.map((it) => (
              <div key={it.product.product_id} className="bg-white rounded-[16px] p-3 flex items-center gap-3" data-testid={`cart-item-${it.product.product_id}`}>
                <ProductImage src={it.product.image} alt={it.product.name} className="w-16 h-16 rounded-[12px] object-cover bg-[#F5F8F6]" iconSize={22} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm text-[#122117] truncate">{it.product.name}</h3>
                  <p className="text-xs text-[#6B8375]">{it.product.unit}</p>
                  <span className="font-extrabold text-[#0B7A3B] text-sm">{money(it.product.price * it.quantity)}</span>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button onClick={() => removeItem(it.product.product_id)} className="text-[#C0392B]" data-testid={`remove-${it.product.product_id}`}>
                    <Trash2 size={16} />
                  </button>
                  <div className="flex items-center gap-1.5 bg-[#DDF0E5] rounded-full p-0.5">
                    <button onClick={() => setQty(it.product.product_id, it.quantity - 1)} className="w-7 h-7 rounded-full bg-white text-[#0B7A3B] flex items-center justify-center active:scale-90" data-testid={`cart-dec-${it.product.product_id}`}>
                      <Minus size={14} />
                    </button>
                    <span className="font-extrabold text-sm text-[#0B7A3B] min-w-[16px] text-center">{it.quantity}</span>
                    <button onClick={() => setQty(it.product.product_id, it.quantity + 1)} className="w-7 h-7 rounded-full bg-[#0B7A3B] text-white flex items-center justify-center active:scale-90" data-testid={`cart-inc-${it.product.product_id}`}>
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={share} disabled={sharing} className="w-full flex items-center justify-center gap-2 border border-[#0B7A3B] text-[#0B7A3B] font-bold rounded-full py-3 active:scale-95 transition-transform" data-testid="share-basket">
            <Share2 size={18} /> {sharing ? "Creating link…" : "Share basket"}
          </button>

          <div className="bg-white rounded-[16px] p-4 space-y-2" data-testid="cart-breakdown">
            <Row label="Subtotal" value={money(subtotal)} />
            <Row label={chargeLabel} value={money(charge)} />
            <div className="h-px bg-[#F5F8F6] my-1" />
            <Row label="Grand total" value={money(grandTotal)} bold />
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#F5F8F6] p-4 pb-safe max-w-md mx-auto">
          <button onClick={checkout} className="w-full bg-[#0B7A3B] text-white font-extrabold rounded-full py-4 flex items-center justify-center gap-2 active:scale-95 transition-transform hover:bg-[#095F2E]" data-testid="cart-checkout">
            Checkout · {money(grandTotal)}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-extrabold text-[#122117] text-lg" : "text-[#6B8375]"}>{label}</span>
      <span className={bold ? "font-extrabold text-[#0B7A3B] text-lg" : "font-semibold text-[#122117]"}>{value}</span>
    </div>
  );
}
